/**
 * Count-up animation for the Company stats section.
 *
 * Deliberately free-standing: no imports from @theme/*, so a Horizon update
 * that reshapes the Component base class can't break this section. Delete this
 * file plus sections/company-stats.liquid and blocks/_stat.liquid to remove the
 * feature entirely.
 *
 * The final value is already in the DOM from Liquid, so the stats are correct
 * with JavaScript disabled, before this module loads, and if parsing fails.
 */

const DURATION = 1600;

/** Matches the first run of digits, allowing thousands separators. */
const NUMBER_PATTERN = /\d[\d,.\s]*/;

/**
 * Splits "5,000+" into the parts needed to rebuild it mid-animation.
 * Returns null when there is no number to animate.
 *
 * @param {string} raw
 * @returns {{ prefix: string, suffix: string, target: number, groups: boolean, decimals: number } | null}
 */
function parseValue(raw) {
  const match = raw.match(NUMBER_PATTERN);
  if (!match || match.index === undefined) return null;

  const matched = match[0];
  // Strip separators to get something Number() can read. A trailing "." or ","
  // belongs to the surrounding text, not the number, so trim it off first.
  const trimmed = matched.replace(/[.,\s]+$/, '');
  const numeric = Number(trimmed.replace(/[,\s]/g, ''));

  if (!Number.isFinite(numeric)) return null;

  return {
    prefix: raw.slice(0, match.index),
    suffix: raw.slice(match.index + trimmed.length),
    target: numeric,
    // Only re-apply grouping if the merchant typed it that way.
    groups: /[,\s]/.test(trimmed),
    // Count up through decimals rather than whole numbers when the value has
    // them, so "8.5" doesn't tick 0→9 and then snap back.
    decimals: (trimmed.split('.')[1] ?? '').length,
  };
}

/** @param {number} t */
function easeOutQuart(t) {
  return 1 - Math.pow(1 - t, 4);
}

class CompanyStats extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {number[]} */
  #frames = [];

  connectedCallback() {
    if (!this.hasAttribute('data-animate')) return;

    // Respect the OS-level setting — the numbers are already correct in the DOM,
    // so skipping the animation costs nothing.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          // Run once, then stop observing.
          this.#observer?.disconnect();
          this.#run();
        }
      },
      { threshold: 0.35 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    for (const frame of this.#frames) cancelAnimationFrame(frame);
    this.#frames = [];
  }

  #run() {
    const numbers = this.querySelectorAll('[data-stat-number]');

    for (const element of numbers) {
      if (!(element instanceof HTMLElement)) continue;

      const raw = element.textContent?.trim() ?? '';
      const parsed = parseValue(raw);

      // Nothing numeric to animate (e.g. "24/7") — leave the value as rendered.
      if (!parsed) continue;

      this.#animate(element, parsed, raw);
    }
  }

  /**
   * @param {HTMLElement} element
   * @param {{ prefix: string, suffix: string, target: number, groups: boolean, decimals: number }} parsed
   * @param {string} original
   */
  #animate(element, parsed, original) {
    const { prefix, suffix, target, groups, decimals } = parsed;
    const start = performance.now();

    /** @param {number} value */
    const format = (value) =>
      groups
        ? value.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
        : value.toFixed(decimals);

    // Reserve the final width so the surrounding layout doesn't reflow as
    // digits are added. Paired with font-variant-numeric: tabular-nums.
    element.style.display = 'inline-block';
    element.style.minWidth = `${element.offsetWidth}px`;

    const step = (/** @type {number} */ now) => {
      const progress = Math.min((now - start) / DURATION, 1);
      const current = easeOutQuart(progress) * target;

      element.textContent = `${prefix}${format(current)}${suffix}`;

      if (progress < 1) {
        this.#frames.push(requestAnimationFrame(step));
      } else {
        // Restore the merchant's exact string, so any formatting we didn't
        // model (odd spacing, non-standard separators) survives intact.
        element.textContent = original;
        element.style.minWidth = '';
      }
    };

    element.textContent = `${prefix}${format(0)}${suffix}`;
    this.#frames.push(requestAnimationFrame(step));
  }
}

if (!customElements.get('company-stats')) {
  customElements.define('company-stats', CompanyStats);
}
